import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";

import { API_ROUTES } from "../../api/ApiRoutes";
import { getPrivateInstance } from "../_privateInstance";

const ISO_DATE_FORMAT = "YYYY-MM-DD";

export type FinancialsMonthlyFilters = {
  start_date: string;
  end_date: string;
};

export type FinancialsMonthlyPoint = {
  month: string;
  total_revenue: number;
  total_purchases: number;
  total_withdrawals: number;
};

type RawMonthlyRow = {
  month?: string | null;
  date?: string | null;
  total_revenue?: number | string | null;
  revenue?: number | string | null;
  total_purchases?: number | string | null;
  deposit_amount?: number | string | null;
  total_deposits?: number | string | null;
  purchases?: number | string | null;
  total_withdrawals?: number | string | null;
  withdrawal_amount?: number | string | null;
  withdrawals?: number | string | null;
};

const coerceNumber = (value: unknown): number => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const normalizeMonthKey = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;

  const parsed = dayjs(trimmed);
  if (!parsed.isValid()) return null;
  return parsed.startOf("month").format(ISO_DATE_FORMAT);
};

const buildMonthRange = (start: string, end: string): string[] => {
  if (!start || !end) return [];

  const startMonth = dayjs(start).startOf("month");
  const endMonth = dayjs(end).startOf("month");
  if (!startMonth.isValid() || !endMonth.isValid()) return [];
  if (endMonth.isBefore(startMonth, "month")) return [];

  const months: string[] = [];
  let cursor = startMonth;
  while (!cursor.isAfter(endMonth, "month")) {
    months.push(cursor.format(ISO_DATE_FORMAT));
    cursor = cursor.add(1, "month");
  }
  return months;
};

const mapRow = (row: RawMonthlyRow): FinancialsMonthlyPoint | null => {
  const monthSource =
    typeof row.month === "string"
      ? row.month
      : typeof row.date === "string"
        ? row.date
        : "";
  const month = normalizeMonthKey(monthSource);
  if (!month) return null;

  return {
    month,
    total_revenue: coerceNumber(row.total_revenue ?? row.revenue),
    total_purchases: coerceNumber(
      row.total_purchases ??
        row.deposit_amount ??
        row.total_deposits ??
        row.purchases,
    ),
    total_withdrawals: coerceNumber(
      row.total_withdrawals ?? row.withdrawal_amount ?? row.withdrawals,
    ),
  };
};

const mergeMonthlyPoint = (
  existing: FinancialsMonthlyPoint | undefined,
  incoming: FinancialsMonthlyPoint,
): FinancialsMonthlyPoint => {
  if (!existing) return incoming;
  return {
    month: incoming.month,
    total_revenue: existing.total_revenue + incoming.total_revenue,
    total_purchases: existing.total_purchases + incoming.total_purchases,
    total_withdrawals: existing.total_withdrawals + incoming.total_withdrawals,
  };
};

const extractRows = (body: unknown): RawMonthlyRow[] => {
  const payload = (body as { data?: unknown })?.data;
  const rawList = Array.isArray(payload) ? payload : [];

  return rawList.filter(
    (entry): entry is RawMonthlyRow =>
      typeof entry === "object" && entry !== null,
  );
};

/**
 * GET /financials/analytics/kpis/monthly (admin-panel-v2).
 *
 * Contract: `{ success, data: MonthlyRow[] }` where each row includes a month
 * identifier plus revenue, purchase/deposit, and withdrawal totals. If the API
 * returns multiple rows for the same month, metrics are summed into one point.
 */
export const parseFinancialsMonthlyResponse = (
  body: unknown,
  filters: FinancialsMonthlyFilters,
): FinancialsMonthlyPoint[] => {
  const byMonth = new Map<string, FinancialsMonthlyPoint>();

  for (const row of extractRows(body)) {
    const point = mapRow(row);
    if (!point) continue;
    byMonth.set(point.month, mergeMonthlyPoint(byMonth.get(point.month), point));
  }

  const denseMonths = buildMonthRange(filters.start_date, filters.end_date);
  if (denseMonths.length > 0) {
    return denseMonths.map(
      (month) =>
        byMonth.get(month) ?? {
          month,
          total_revenue: 0,
          total_purchases: 0,
          total_withdrawals: 0,
        },
    );
  }

  return Array.from(byMonth.values()).sort((a, b) =>
    a.month.localeCompare(b.month),
  );
};

export const fetchFinancialsMonthly = async (
  filters: FinancialsMonthlyFilters,
): Promise<FinancialsMonthlyPoint[]> => {
  const instance = getPrivateInstance();
  const response = await instance.get(API_ROUTES.FINANCIALS_MONTHLY, {
    params: filters,
  });
  return parseFinancialsMonthlyResponse(response?.data, filters);
};

export const useGetFinancialsMonthly = (filters: FinancialsMonthlyFilters) => {
  const { start_date, end_date } = filters;

  return useQuery<FinancialsMonthlyPoint[], Error>({
    queryKey: ["FINANCIALS_MONTHLY", start_date, end_date],
    queryFn: () => fetchFinancialsMonthly(filters),
    enabled: Boolean(start_date && end_date),
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });
};
